"use client";

import React, { createContext, useContext } from "react";
import useLocalStorage from "@/app/hooks/useLocalStorage";
import useExtendedCarDataListener from "@/hooks/useExtendedCarDataListener";
import { getRegionStates, normalizeMarketState } from "@/lib/envConfig";
import {
  matchesSellerScope,
  normalizeSellerScope,
  SELLER_SCOPE,
} from "@/app/helpers/sellerScope";

const FilterContext = createContext();
export const useFilter = () => {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error("useFilter must be used within a FilterProvider");
  return ctx;
};

// Try a few common places where the state could be stored on the vehicle
function getVehicleState(vehicle) {
  const candidates = [vehicle?.fahrzeug_eigentum?.address?.state];
  const raw = candidates.find((s) => typeof s === "string" && s.trim());
  return normalizeMarketState(raw);
}

export function FilterProvider({
  children,
  sourceCars,
  storageNS = "default", // NEW: namespace for LS keys (e.g. "live", "extended")
}) {
  // if sourceCars not passed, fallback to extended cars (keeps previous behavior)
  const { carData: extendedCars = [] } = useExtendedCarDataListener("extended");
  const allCars = Array.isArray(sourceCars) ? sourceCars : extendedCars;
  const regionStates = getRegionStates();

  // helper to namespace LS keys per page
  const k = (key) => `${storageNS}:${key}`;

  // --------- existing filter states ----------
  const [selectedBrands, setSelectedBrands] = useLocalStorage(
    k("filters_brands"),
    []
  );
  const [regFrom, setRegFrom] = useLocalStorage(k("filters_regFrom"), null);
  const [regTo, setRegTo] = useLocalStorage(k("filters_regTo"), null);
  const [brandQuery, setBrandQuery] = useLocalStorage(
    k("filters_brandQuery"),
    ""
  );
  const [selectedTransmissions, setSelectedTransmissions] = useLocalStorage(
    k("filters_transmissions"),
    []
  );
  const [selectedFueltypes, setSelectedFueltypes] = useLocalStorage(
    k("filters_fueltypes"),
    []
  );
  const [selectedWarantys, setSelectedWarantys] = useLocalStorage(
    k("filters_warantys"),
    []
  );
  const [selectedConditions, setSelectedConditions] = useLocalStorage(
    k("filters_conditions"),
    []
  );
  const [kmFrom, setKmFrom] = useLocalStorage(k("filters_kmFrom"), null);
  const [kmTo, setKmTo] = useLocalStorage(k("filters_kmTo"), null);
  const [priceFrom, setPriceFrom] = useLocalStorage(
    k("filters_priceFrom"),
    null
  );
  const [priceTo, setPriceTo] = useLocalStorage(k("filters_priceTo"), null);
  const [isSavePopupOpen, setIsSavePopupOpen] = useLocalStorage(
    k("filters_savePopup"),
    false
  );

  // ✅ selectedStates (Bundesländer)
  const [selectedStates, setSelectedStates] = useLocalStorage(
    k("filters_states"),
    []
  );

  // ✅ NEW: max distance (in km). null = no limit
  const [distanceMaxKm, setDistanceMaxKm] = useLocalStorage(
    k("filters_distanceMaxKm"),
    null
  );

  const [storedSellerScope, setStoredSellerScope] = useLocalStorage(
    k("filters_sellerScope"),
    SELLER_SCOPE.ALL
  );
  const sellerScope = normalizeSellerScope(storedSellerScope);
  const setSellerScope = (value) =>
    setStoredSellerScope(normalizeSellerScope(value));

  // ---------- filtering (WITHOUT distance; distance is applied where we have the distances map) ----------
  const filteredCars = allCars.filter((vehicle) => {
    if (!matchesSellerScope(vehicle, sellerScope)) return false;

    // brand
    if (selectedBrands.length && !selectedBrands.includes(vehicle?.marke))
      return false;

    // first registration year
    const year = new Date(
      vehicle?.vehicleFeatures?.firstRegistrationDate
    ).getFullYear?.()
      ? new Date(vehicle?.vehicleFeatures?.firstRegistrationDate).getFullYear()
      : null;
    if (regFrom !== null && year !== null && year < regFrom) return false;
    if (regTo !== null && year !== null && year > regTo) return false;

    // transmission
    if (
      selectedTransmissions.length &&
      !selectedTransmissions.includes(vehicle?.gearbox)
    )
      return false;

    // fuel
    if (
      selectedFueltypes.length &&
      !selectedFueltypes.includes(vehicle?.fueltype)
    )
      return false;

    // warranty
    if (
      selectedWarantys.length &&
      !selectedWarantys.includes(vehicle?.vehicleFeatures?.garantie)
    )
      return false;

    // condition (note: UI stores lowercase "gold/silber/bronze")
    if (
      selectedConditions.length &&
      !selectedConditions.includes((vehicle?.carRating || "").toLowerCase())
    )
      return false;

    // ✅ state
    const state = getVehicleState(vehicle); // normalized label or null
    if (selectedStates.length && !selectedStates.includes(state)) return false;

    // km
    const km = Number(`${vehicle?.kmStand ?? ""}`.replace(/\D/g, "")) || 0;
    if (kmFrom !== null && km < kmFrom) return false;
    if (kmTo !== null && km > kmTo) return false;

    // price
    const priceRaw = vehicle?.bidsData?.currentBid ?? vehicle?.startPrice;
    const price = Number(`${priceRaw ?? ""}`.replace(/\D/g, ""));
    if (priceFrom !== null && price < priceFrom) return false;
    if (priceTo !== null && price > priceTo) return false;

    // NOTE: distance filter *not* applied here (we don't have the distances map here).
    // It is applied in Auktion.jsx using distanceMaxKm + distances.

    return true;
  });

  const value = {
    allCars,
    filteredCars,

    // existing
    selectedBrands,
    setSelectedBrands,
    regFrom,
    setRegFrom,
    regTo,
    setRegTo,
    brandQuery,
    setBrandQuery,
    selectedTransmissions,
    setSelectedTransmissions,
    selectedFueltypes,
    setSelectedFueltypes,
    selectedWarantys,
    setSelectedWarantys,
    selectedConditions,
    setSelectedConditions,
    kmFrom,
    setKmFrom,
    kmTo,
    setKmTo,
    priceFrom,
    setPriceFrom,
    priceTo,
    setPriceTo,
    isSavePopupOpen,
    setIsSavePopupOpen,

    // states + constants
    selectedStates,
    setSelectedStates,
    regionStates,

    // ✅ distance
    distanceMaxKm,
    setDistanceMaxKm,

    // customer/dealer source
    sellerScope,
    setSellerScope,
  };

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}
